import React, { useEffect, useState, useRef } from "react";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import { useToasts } from "react-toast-notifications";
import { colors } from "../../../helpers/colors";
import "../../shared/GuidedInput.css";
import ConfirmResetModal from "../../shared/ConfirmResetModal";
import { LABEL_FIELD } from "../../shared/labelField";

const Input = (props) => {
  const { addToast } = useToasts();
  const [currentDimension, setCurrentDimension] = useState({});
  const [activeFieldIndex, setActiveFieldIndex] = useState(0);
  const [completedFields, setCompletedFields] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const inputRefs = useRef({});

  const isFirstTrace = parseInt(props.currenttrace) === 0;

  // Decimal fields use type="text" + inputMode="decimal" rather than
  // type="number": a native number input sanitises the in-progress value "3."
  // to "" on the decimal keystroke, so at normal typing speed the "." is
  // swallowed and decimals like 2.5 can never be entered.
  const geometryFields = [
    { id: "direction", name: "direction", label: "View Direction", type: "number", step: 1 },
    { id: "radius", name: "radius", label: "Shaft Radius", type: "text", inputMode: "decimal" },
    { id: "startdepth", name: "startdepth", label: "Start Depth", type: "text", inputMode: "decimal" },
    { id: "enddepth", name: "enddepth", label: "End Depth", type: "text", inputMode: "decimal" },
  ];

  const sharedFields = [
    { id: "dir", name: "dir", label: "Discontinuity Dip Direction", type: "number", step: 1 },
    { id: "amt", name: "amt", label: "Discontinuity Dip Amount", type: "number", step: 1 },
    { id: "depth", name: "depth", label: "Min depth", type: "text", inputMode: "decimal" },
    LABEL_FIELD,
  ];

  const visibleFields = isFirstTrace ? [...geometryFields, ...sharedFields] : sharedFields;
  const activeField = visibleFields[activeFieldIndex];

  useEffect(() => {
    let temp = { ...props.dimensions[props.currenttrace] };
    setCurrentDimension(temp);
  }, [props.currenttrace, props.dimensions]);

  // Coming back from the output via "Edit Input" opens the form with every
  // field filled and editable; a new trace starts the guided walk afresh.
  useEffect(() => {
    setCompletedFields(props.isEditInput ? new Set(visibleFields.map((f) => f.id)) : new Set());
    setActiveFieldIndex(0);
    setIsEditMode(Boolean(props.isEditInput));
  }, [props.currenttrace]);

  useEffect(() => {
    if (activeFieldIndex >= visibleFields.length && visibleFields.length > 0) {
      setActiveFieldIndex(Math.max(0, visibleFields.length - 1));
    }
  }, [visibleFields.length, activeFieldIndex]);

  useEffect(() => {
    if (!activeField || !inputRefs.current[activeField.id]) return;
    const el = inputRefs.current[activeField.id];
    const scrollPadding = 80;
    requestAnimationFrame(() => {
      el.focus();
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= scrollPadding && rect.bottom <= window.innerHeight - scrollPadding;
      if (!inView) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    });
  }, [activeFieldIndex, activeField]);

  const updateCurrentTrace = (e) => {
    props.setcurrenttrace(e.target.value);
  };

  const validateField = (fieldId) => {
    const dim = currentDimension;
    let error = "";
    switch (fieldId) {
      case "direction":
        if (dim.direction === "" || dim.direction === undefined || dim.direction === null) {
          error = "Please enter view direction";
        } else if (dim.direction < 0 || dim.direction > 359) {
          error = "Please choose value between 0 and 359";
        }
        break;
      case "radius":
        if (!(parseFloat(dim.radius) > 0)) {
          error = "Shaft radius must be greater than 0";
        }
        break;
      case "startdepth":
        if (parseFloat(dim.startdepth) < 0) {
          error = "Start depth cannot be negative";
        }
        break;
      case "enddepth":
        if (parseFloat(dim.enddepth) <= parseFloat(dim.startdepth)) {
          error = "End depth must be greater than start depth (" + dim.startdepth + ")";
        }
        break;
      case "dir":
        if (dim.dir === "" || dim.dir === undefined || dim.dir === null) {
          error = "Please enter dip direction";
        } else if (dim.dir < 0 || dim.dir > 360) {
          error = "Value must be between 0 and 360";
        }
        break;
      case "amt":
        if (dim.amt === "" || dim.amt === undefined || dim.amt === null) {
          error = "Please enter dip amount";
        } else if (dim.amt < 0 || dim.amt > 90) {
          error = "Value must be between 0 and 90";
        }
        break;
      case "depth":
        if (parseFloat(dim.depth) < parseFloat(dim.startdepth) || parseFloat(dim.depth) > parseFloat(dim.enddepth)) {
          const sd = currentDimension.startdepth ?? props.dimensions[0]?.startdepth;
          const ed = currentDimension.enddepth ?? props.dimensions[0]?.enddepth;
          error = "Min depth must be between " + sd + " and " + ed;
        }
        break;
      default:
        break;
    }

    if (error) {
      if (window.audio) window.audio.play();
      addToast(error, {
        appearance: "error",
      });
      return false;
    }
    return true;
  };

  const handleFieldChange = (e) => {
    // The decimal fields are type="text" (so the in-progress "3." survives),
    // so re-impose numeric-only entry here: ignore keystrokes that would make
    // the value non-numeric while still allowing a trailing "." mid-typing.
    const field = visibleFields.find((f) => f.name === e.target.name);
    if (field && field.inputMode === "decimal" && e.target.value !== "" && !/^\d*\.?\d*$/.test(e.target.value)) {
      return;
    }
    props.updatedimension(e);
  };

  const handleKeyDown = (e, fieldId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      tryAdvance(fieldId);
    }
  };

  const tryAdvance = (fieldId) => {
    if (validateField(fieldId)) {
      const nextCompleted = new Set([...completedFields, fieldId]);
      setCompletedFields(nextCompleted);
      if (activeFieldIndex < visibleFields.length - 1) {
        setActiveFieldIndex(activeFieldIndex + 1);
      } else if (visibleFields.every((f) => nextCompleted.has(f.id))) {
        const idx = getFirstInvalidFieldIndex();
        if (idx >= 0) {
          setActiveFieldIndex(idx);
          return;
        }
        if (isEditMode) setIsEditMode(false);
        props.showoutput();
      }
    }
  };

  const handleFieldClick = (index) => {
    if (isEditMode || index <= activeFieldIndex + 1) {
      setActiveFieldIndex(index);
    }
  };

  const isAllComplete = () => visibleFields.every((f) => completedFields.has(f.id));

  const getFirstInvalidFieldIndex = () => {
    for (let i = 0; i < visibleFields.length; i++) {
      if (!validateField(visibleFields[i].id)) return i;
    }
    return -1;
  };

  const handleNext = () => {
    if (isEditMode) {
      const idx = getFirstInvalidFieldIndex();
      if (idx >= 0) {
        setActiveFieldIndex(idx);
        return;
      }
      setIsEditMode(false);
      props.showoutput();
      return;
    }

    if (activeField && !completedFields.has(activeField.id)) {
      if (!validateField(activeField.id)) return;
      setCompletedFields((prev) => new Set([...prev, activeField.id]));
    }

    const idx = getFirstInvalidFieldIndex();
    if (idx >= 0) {
      setActiveFieldIndex(idx);
      return;
    }

    props.showoutput();
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setActiveFieldIndex(0);
    setCompletedFields(new Set(visibleFields.map((f) => f.id)));
  };

  // Reset Values asks first; only a confirmed reset clears the form.
  const [confirmingReset, setConfirmingReset] = useState(false);

  const handleReset = () => {
    props.resetvalues();
    setIsEditMode(false);
    setActiveFieldIndex(0);
    setCompletedFields(new Set());
  };

  const getFieldClass = (index, fieldId) => {
    if (isEditMode) {
      return index === activeFieldIndex
        ? "guided-field guided-field--active"
        : "guided-field guided-field--complete";
    }
    if (index === activeFieldIndex) return "guided-field guided-field--active";
    if (completedFields.has(fieldId)) return "guided-field guided-field--complete";
    return "guided-field guided-field--pending";
  };

  const renderField = (field, index) => {
    const isActive = index === activeFieldIndex;
    const isComplete = completedFields.has(field.id);
    const isReadOnly = !isFirstTrace && geometryFields.some((f) => f.id === field.id);

    return (
      <div key={field.id} className="guided-field-wrap">
        {isActive && !isComplete && (
          <span className="input-please">Input Please</span>
        )}
        <div
          className={getFieldClass(index, field.id)}
          onClick={() => handleFieldClick(index)}
        >
          <div className="guided-field__label">
            <span>{field.label}</span>
          </div>

          <input
            ref={(el) => (inputRefs.current[field.id] = el)}
            type={field.type}
            inputMode={field.inputMode}
            id={field.id}
            name={field.name}
            className="guided-field__input"
            value={currentDimension[field.name] ?? ""}
            onChange={handleFieldChange}
            onKeyDown={(e) => handleKeyDown(e, field.id)}
            step={field.step}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            readOnly={isReadOnly}
          />
          {field.id === LABEL_FIELD.id && (
            <small className="guided-field-hint">
              Optional. Your name for this discontinuity, shown in the Inputs and Outputs tables.
            </small>
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="page">
      <Header onBack={props.showobjective} />
      <Footer />
      <ConfirmResetModal
        show={confirmingReset}
        onCancel={() => setConfirmingReset(false)}
        onConfirm={() => {
          setConfirmingReset(false);
          handleReset();
        }}
      />
      <div className="page-content header-clear-medium">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card card-style contact-form">
              <div className="content guided-uniform">
                {props.tracecount > 0 && (
                  <div className="guided-field-wrap">
                    <div className="guided-field guided-field--complete">
                      <div className="guided-field__label">
                        <span>Select Trace</span>
                      </div>
                      <select
                        className="guided-field__select"
                        onChange={updateCurrentTrace}
                        value={props.currenttrace}
                        name="trace"
                        id="trace"
                      >
                        {props.dimensions.map((dimension, index) => (
                          <option style={{ color: colors[index] }} key={index} value={index}>
                            Trace {index + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {visibleFields.map((field, index) => renderField(field, index))}

                <div className="guided-input-actions mt-4">
                  <button
                    type="button"
                    className="btn bg-red-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={() => setConfirmingReset(true)}
                  >
                    Reset Values
                  </button>
                  <button
                    type="button"
                    className="btn bg-secondary text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn bg-green-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleNext}
                    disabled={!isEditMode && !isAllComplete() && activeFieldIndex < visibleFields.length - 1}
                  >
                    {props.label || "Next"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Input;
